<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import Footer from '@components/Footer'
import Navbar from '@components/navbar/navbar'
import client from './sanityConfig'
import imageUrlBuilder from '@sanity/image-url'
import bugsnag from '@bugsnag/js'
import BugsnagVue from '@bugsnag/plugin-vue'
import Translate from './components/translate'
import Polyglot from 'node-polyglot'
import languages from './locales'
import api from './api'

// lazyload modals
const NewSignup = () => import('@components/NewSignup')
const Login = () => import('@components/Login')
const Signup = () => import('@components/Signup')
const ChallengePurchase = () => import('@components/challenge-purchase/challenge-purchase')
const ForgotPassword = () => import('@components/ForgotPassword')

const builder = imageUrlBuilder(client)

const axios = require('axios')

if (process.env.NODE_ENV === 'production') {
  bugsnag.start({ // init bugsnag in production only
    apiKey: process.env.VUE_APP_BUGSNAG_KEY,
    notifyReleaseStages: ['production', 'staging'],
    plugins: [new BugsnagVue(Vue)],
    releaseStage: process.env.VUE_APP_BUGSNAG_RELEASE
  })
}

export default {
  name: 'App',
  data () {
    return {
      initing: true
    }
  },
  components: {
    Login,
    NewSignup,
    Signup,
    Footer,
    Navbar,
    ForgotPassword,
    ChallengePurchase,
    Translate
  },
  computed: {
    ...mapState(['showAlert', 'fromChallenge', 'settings', 'loggedIn', 'user']),
    twitterCardImage () {
      return (
        this.settings &&
        this.settings.twitterCards &&
        builder.image(this.settings.twitterCards.image).url()
      )
    },
    FacebookOPImage () {
      return (
        this.settings &&
        this.settings.facebookOpenGraphs &&
        builder.image(this.settings.facebookOpenGraphs.image).url()
      )
    }
  },
  async created () {
    // Force deploy
    var language = window.navigator.userLanguage || window.navigator.language
    console.log(language) // works IE/SAFARI/CHROME/FF
    this.$store.commit('setLocale', language.includes('-') ? language.split('-')[0] : language)

    let polyglot
    if (this.$store && this.$store.state.locale && languages[this.$store.state.locale]) {
      // We support this language. Go ahead
      polyglot = new Polyglot({ phrases: languages[this.$store.state.locale], locale: this.$store.state.locale })
    } else {
      // Default to english
      polyglot = new Polyglot({ phrases: languages.en, locale: 'en' })
    }
    Vue.prototype.$_polyglot = polyglot
    if (this.$router.history.current.name === 'Challenge') {
      this.$store.commit('setFromChallenge', true)
    } else {
      this.$store.commit('setFromChallenge', false)
    }

    if (localStorage.getItem('pcc-user')) {
      const user = await JSON.parse(localStorage.getItem('pcc-user'))
      const response = await this.$store.dispatch('getUser', user.token)
      if (!(response && response.data && response.data.success)) {
        this.$router.push('/').catch(() => {})
      }
    }

    const getSettings = '*[_type in ["settings"]]'

    const settings = await client.fetch(getSettings)

    this.$store.commit('setAppSettings', settings[0])

    if (settings[0] && settings[0].favicon) {
      var link = document.querySelector("link[rel*='icon']") || document.createElement('link')
      link.type = 'image/x-icon'
      link.rel = 'shortcut icon'
      var url = builder.image(settings[0].favicon)
      link.href = url
      document.getElementsByTagName('head')[0].appendChild(link)
    }

    const getPages = '*[_type in ["page"]]'

    const pages = await client.fetch(getPages)

    this.$store.commit('setPageContent', pages)

    const getGates = '*[_type in ["gate"]]'

    const gates = await client.fetch(getGates)

    this.$store.commit('setGates', gates)

    this.initing = false

    this.fetchStripeData()
  },
  methods: {
    async fetchStripeData () {
      const url = api.URL_FETCH_PRODUCTS.replace(':trainerID', process.env.VUE_APP_TRAINER_ID)
      let stripeResponse = await axios.get(url)

      // Recall stripe subscription if no data for two times + ^^
      for (let i = 0; i < 2 && stripeResponse.data.subscriptions.length === 0; i++) {
        stripeResponse = await axios.get(url)
      }

      // Fire bugsnag error if still no subscriptions comming from stripe
      if (stripeResponse.data.subscriptions.length === 0) {
        bugsnag.notify(new Error('Unable to get subscriptions'))
      } else {
        this.$store.commit('setSubscriptions', stripeResponse.data.subscriptions)
        this.$store.commit('setChallenges', stripeResponse.data.challengeProducts)
        this.$store.commit('setTrialDays', stripeResponse.data.trial_days_data.trial_days)
      }
    }
  },
  // for social media meta fields - enabled via (vue-meta)
  // it is a function so we access (this)!
  metaInfo () {
    return {
      // content can access (this) and read from sanity
      meta: [
        // Twitter
        {
          name: 'twitter:title',
          content: this.settings?.twitterCards?.title
        },
        {
          name: 'twitter:description',
          content: this.settings?.twitterCards?.description
        },
        {
          name: 'twitter:image',
          content: this.twitterCardImage
        },
        {
          name: 'twitter:image:alt',
          content: this.settings?.twitterCards?.imageAlt
        },
        // Facebook
        {
          property: 'og:title',
          content: this.settings?.facebookOpenGraphs?.title
        },
        {
          property: 'og:description',
          content: this.settings?.facebookOpenGraphs?.description
        },
        {
          property: 'og:image',
          content: this.FacebookOPImage
        },
        {
          property: 'og:image:alt',
          content: this.settings?.facebookOpenGraphs?.imageAlt
        }
      ]
    }
  }
}
</script>

<template>
  <div id="app">
    <Navbar v-if="
      $route &&
      $route.name &&
      ($route.name !== 'Subscribtion Confirmation' && $route.name !== 'Challenge Confirmation' && $route.name !== 'Challenge')"/>
    <b-alert v-model="showAlert" id="alert" variant="success" dismissible>
      <Translate directive="modal.reset.title" />
    </b-alert>
    <router-view />
    <Footer/>
    <template>
      <b-modal size="lg" hide-header centered scrollable id="modal-login" :static="true" :lazy="true" hide-footer>
        <Login />
      </b-modal>
      <b-modal size="lg" hide-header centered scrollable id="modal-signup" :static="true" :lazy="true" hide-footer>
        <Signup />
      </b-modal>
      <b-modal size="md" hide-header centered scrollable id="modal-challenge-purchase" :static="true" :lazy="true" hide-footer>
        <ChallengePurchase />
      </b-modal>
      <b-modal size="lg" scrollable centered id="modal-forgotpw" :static="true" :lazy="true" hide-footer>
        <ForgotPassword />
      </b-modal>
    </template>
    <template>
      <b-modal size="lg" hide-header centered scrollable id="new-modal-signup" :static="true" :lazy="true" hide-footer>
        <NewSignup />
      </b-modal>
    </template>
  </div>
</template>

<style lang="scss">
  @import '~normalize.css/normalize.css';

  *{
  -webkit-backface-visibility:hidden;
     -moz-backface-visibility:hidden;
      -ms-backface-visibility:hidden;
       -o-backface-visibility:hidden;
          backface-visibility:hidden;
  }

  // Bootstrap Vue
  @import '~bootstrap/scss/bootstrap';
  @import '~bootstrap-vue/src/index.scss';
  section {
    padding: 60px 0;
  }
  .btn {
    margin-top: 20px;
    padding: 1rem 2rem;
    border: none;
    letter-spacing: 2px;
    min-height: 40px;
    border-radius:15px;
    background-color: var(--button-color);
    text-transform: uppercase;
    color: white;
    min-height: 40px;
    -webkit-font-smoothing: antialiased;
    &:hover {
      background-color: var(--button-hover);
    }
    &:disabled {
      background-color: grey;
      cursor: not-allowed;
    }
  }
  #alert {
    z-index: 999;
    position: fixed;
    top: 65px;
    width: 100%;
  }

#modal-challenge-purchase {
  .modal-dialog {
    max-width: 700px;
  }
  .modal-body {
    padding: 0;
  }
  .container {
    max-width: unset !important;
  }
}

  .modal-header {
    border-bottom: none;
    position: absolute;
    text-align: center;
    right: 0;
    transition: height 300ms;
    z-index: 2;
    button {
      margin: 0;
    }
    .close {
      opacity: 1;
      margin: 0;
      padding-left: 1.4rem;
    }
  }
  .modal-content {
    min-height: 35vh;
    border-radius: 24px;
    background-color: white;
    border: none;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: 0 auto;
  }

  .modal-lg { max-width: 630px;}
  @media (max-width:768px){
    section{
      padding:2rem 0;
    }
    ul{
      list-style-position:inside;
      padding-left:0;
    }
    ul li{
      margin-bottom:0.5rem;
    }
  }
  #modal-challenge-purchase .modal-dialog {
    position: relative;
  }
  #new-modal-signup {
    .modal-dialog {
      max-width: 100%;
      height: 100%;
      min-height: 100%;
      margin: 0;
      padding: 0;
      .modal-content {
        height: auto;
        min-height: 100%;
        border-radius: 0;
        .modal-body {
          padding: 0;
        }
      }
    }
  }
</style>
